import { Button, Card, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { getProductPictogramId, productIds } from '@eti/products';
import { Pictogram } from '@eti/components';
import { useDirection, useSiteContext, useSnowplowTracking, useTranslation } from '@eti/providers';
import { Link } from 'react-router-dom';
import type { AvailableExtraProductsType } from '../../models';
import TitledCard from '../../../../common/components/TitledCard';
import { routes } from '../../../../constants/routesAndApisConstants';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import Loading from '../../../../common/loading/components/Loading';

const cardStyles = (numOfCards: number) => css`
  border: 1px solid ${vars.colors.divider};

  @media (min-width: 1170px) {
    width: calc((100% - (16px * (${numOfCards} - 1))) / ${numOfCards});
  }
`;

const loadingStyles = css`
  font-size: 1.5rem;
  padding: 24px;
`;

const outerStackStyles = css`
  height: 100%;
  justify-content: space-between;
`;

const upsellProductTitleStyles = css`
  word-break: break-word;
`;

interface UpsellProductsProps {
  products?: AvailableExtraProductsType;
}

const UpsellProducts = ({ products }: UpsellProductsProps) => {
  const { t } = useTranslation();
  const { brand } = useSiteContext();
  const isRTL = useDirection();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const { ancillaryAvailabilityLoading } = useOrderDetails();

  const handleSnowplowOnAddProduct = (code: string) => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Overview',
        action: 'Click',
        label: `ADD_${code}`,
      });
    }
  };

  const handleSnowplowOnAllProducts = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Overview',
        action: 'Click',
        label: 'EXTRA_PRODUCTS_CTA',
      });
    }
  };

  return (
    <TitledCard data-testid="upsell-products" title={t('OrderDetails.UpsellProducts.Title')}>
      {ancillaryAvailabilityLoading && (
        <Loading className={loadingStyles} dataTestId="upsell-products-loading" />
      )}
      {!ancillaryAvailabilityLoading && products && products.length > 0 && (
        <Stack>
          <Inline>
            {products.map((product) => (
              <Card className={cardStyles(products.length)} key={product.id}>
                <Stack className={outerStackStyles} spacing={8}>
                  <Stack spacing={8}>
                    <Pictogram
                      brand={brand.code}
                      data-testid={`upsell-product-${product.id}-icon`}
                      id={getProductPictogramId(productIds[Number(product.id)], isRTL)}
                      size="2rem"
                    />
                    <Stack spacing={4}>
                      <Text
                        className={upsellProductTitleStyles}
                        data-testid={`upsell-product-${product.id}-title`}
                        variant={textVariants._16_BOLD}
                      >
                        {product.name}
                      </Text>
                      <Text data-testid={`upsell-product-${product.id}-description`}>
                        {product.texts.smallCardText}
                      </Text>
                    </Stack>
                  </Stack>
                  <Inline align="end" alignY="center" spacing={8}>
                    <Button
                      as={Link}
                      data-testid={`upsell-product-${product.id}-link`}
                      onClick={() => handleSnowplowOnAddProduct(product.code)}
                      size="small"
                      to={`${routes.ORDER_DETAILS_EXTRA_PRODUCTS}#product-id-${product.id}`}
                      variant="primary"
                    >
                      {t('OrderDetails.WhatIsIncluded.NotIncluded.Buy.Button.Label')}
                    </Button>
                  </Inline>
                </Stack>
              </Card>
            ))}
          </Inline>
          <Inline align="end" alignY="center" spacing={8}>
            <Button
              as={Link}
              data-testid="upsell-products-see-all-link"
              onClick={handleSnowplowOnAllProducts}
              to={`${routes.ORDER_DETAILS_EXTRA_PRODUCTS}#extraProductsForm`}
              variant="secondary"
            >
              {t('OrderDetails.WhatIsIncluded.NotIncluded.AllProducts.Button.Label')}
            </Button>
          </Inline>
        </Stack>
      )}
      {!ancillaryAvailabilityLoading && (!products || products.length === 0) && (
        <Text data-testid="upsell-products-not-available">
          {t('OrderDetails.UpsellProducts.NotAvailable')}
        </Text>
      )}
    </TitledCard>
  );
};

export default UpsellProducts;
